import { PropsWithChildren, useRef, useEffect } from 'react'
import { useEnforceBlazeFreeTrial } from 'hooks/useEnforceBlazeFreeTrial'
import { Redirect, useRouteMatch } from 'react-router-dom'
import { PAYWALL_START_FREE_TRIAL_PATH, PAYWALL_SUBSCRIBE_PATH, DESIGN_PATH } from 'paths'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { usePaywall } from 'components/TimeTriggerPaywall/hooks/usePaywall'
import { useRootStore } from 'stores/RootStore'
import { useLoggedIn } from 'hooks/useLoggedIn'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useAuth } from 'hooks/UseAuth'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'

const PAYWALL_DISMISSED_KEY = 'paywall_dismissed'

export const PaywallRedirect = ({ children }: PropsWithChildren) => {
  const { showPaywall, canStartFreeTrial } = useEnforceBlazeFreeTrial()
  const isNewRegistration = useFlipperFlag('new_registration')
  const { openPaywall } = usePaywall()
  const featureFlags = useRootStore().featureFlags
  const paywallOpened = useRef(false)
  const isLoggedIn = useLoggedIn()
  const workspace = useCurrentWorkspace()
  const auth = useAuth()
  const designRouteMatch = useRouteMatch<{ key: string }>(DESIGN_PATH)

  const showNewPaywall =
    isNewRegistration &&
    isLoggedIn &&
    (!(workspace?.paying || workspace?.early_adopter) || workspace?.id === ALMANAC_CORE_WORKSPACE_ID)

  // Check if user is a guest (authenticated but not a workspace member) accessing a design
  const isGuestUser = auth && !workspace?.role
  const isAccessingDesign = !!designRouteMatch

  // Don't show paywall for guest users accessing designs (they should be able to view shared designs)
  const shouldSkipPaywallForGuest = isGuestUser && isAccessingDesign

  useEffect(() => {
    if (!featureFlags.ready) return

    // Only show the paywall once for new registrations
    if (showNewPaywall && isNewRegistration && !paywallOpened.current) {
      // Check if we've already shown the paywall in a previous session
      const paywallDismissed = localStorage.getItem(PAYWALL_DISMISSED_KEY) === 'true'

      if (!paywallDismissed) {
        openPaywall()
        paywallOpened.current = true

        // Store the dismissal state in localStorage
        localStorage.setItem(PAYWALL_DISMISSED_KEY, 'true')
      }
    }
  }, [showNewPaywall, isNewRegistration, openPaywall, featureFlags.ready])

  if (!featureFlags.ready) {
    return <>{children}</>
  }

  if (showPaywall && !shouldSkipPaywallForGuest) {
    if (isNewRegistration) {
      return <>{children}</>
    }

    if (canStartFreeTrial) {
      return <Redirect to={PAYWALL_START_FREE_TRIAL_PATH} />
    }

    return <Redirect to={PAYWALL_SUBSCRIBE_PATH} />
  }

  return <>{children}</>
}
